<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'SendMails',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    items: {
      type: Array,
      required: true,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const selectedTemplate = ref(null);
  const templates = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    selectedTemplate: selectedTemplate.value,
    templates: templates.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_templates = () => {
    return $fn.value
      .fetch('list_template')
      .then((data) => _.filter(data, { template_type: 'mjml' }));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedTemplate' },
      { name: 'templates' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { templates: $$load_templates },
    ux: {},
    dataRefs: {
      selectedTemplate,
      templates,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Send emails') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_tst062pi3_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_register_p35rfza2z_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'resultLength',
      value: $event.data.response.data ? $event.data.response.data.length : 0,
    };
  };
  const act_1040167445267876_notification_mwhl3f2dd_getActionArgs = async (
    $event
  ) => {
    return {
      message: $fn.value.ngettext(
        'One email has been successfully enqueued.',
        '${n} emails have been successfully enqueued.',
        $event.data.resultLength
      ),
    };
  };
  const act_1040167445267876_notification_mwhl3f2dd_getWhen = async (
    $event
  ) => {
    return $event.data.resultLength > 0;
  };
  const act_1040167445267876_reloadGrid_4f7bga5gs_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'all',
    };
  };
  const act_1040167445267876_closeComponent_gru2a2dt1_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_1040167445267876_request_fwq1qrobg_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_a_job',
      data: _.chain(props.items)
        .filter((item) => item.customer_email)
        .map((item) => ({
          job_type: 'sendmail:dunning_letter',
          job_data: {
            id: item.id,
            template: selectedTemplate.value,
            mail_to: item.customer_email,
          },
        }))
        .value(),
      parameters: {
        job_type: 'sendmail:dunning_letter',
      },
    };
  };

  const act_7315092382398562_closeModal_tst062pi3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_tst062pi3_getActionArgs,
    });
  const inst_evh_7315092382398562_close_95kq15q0n =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_tst062pi3,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_95kq15q0n],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_oqzeknxv4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_i8czmwqlk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_oqzeknxv4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_i8czmwqlk],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_t55rgb2kc =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_7egxxfo2f =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_t55rgb2kc,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_7egxxfo2f],
    event: 'close',
  });
  const act_1040167445267876_register_p35rfza2z =
    new actionTypes.RegisterAction({
      actionArgs: act_1040167445267876_register_p35rfza2z_getActionArgs,
    });
  const inst_evh_1040167445267876_success_lwq2fxpvx =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_register_p35rfza2z,
      event: 'success',
      displayName: 'register',
    });
  const act_1040167445267876_notification_mwhl3f2dd =
    new actionTypes.NotificationAction({
      actionArgs: act_1040167445267876_notification_mwhl3f2dd_getActionArgs,
      when: act_1040167445267876_notification_mwhl3f2dd_getWhen,
    });
  const inst_evh_1040167445267876_success_itloszxcx =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_notification_mwhl3f2dd,
      event: 'success',
      displayName: 'notification',
    });
  const act_1040167445267876_reloadGrid_4f7bga5gs =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_4f7bga5gs_getActionArgs,
    });
  const inst_evh_1040167445267876_success_1zv4vncjb =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_4f7bga5gs,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_closeComponent_gru2a2dt1 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_gru2a2dt1_getActionArgs,
    });
  const inst_evh_1040167445267876_success_9ye9qvljl =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_gru2a2dt1,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_request_fwq1qrobg = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_fwq1qrobg_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_success_lwq2fxpvx,
      inst_evh_1040167445267876_success_itloszxcx,
      inst_evh_1040167445267876_success_1zv4vncjb,
      inst_evh_1040167445267876_success_9ye9qvljl,
    ],
  });
  const inst_evh_1040167445267876_submit_d2lnf77l9 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_fwq1qrobg,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_d2lnf77l9],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_am9j4vekd =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_x65fw8art =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_am9j4vekd,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_x65fw8art],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_95kq15q0n,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_i8czmwqlk,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_7egxxfo2f,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_lwq2fxpvx,
    inst_evh_1040167445267876_success_itloszxcx,
    inst_evh_1040167445267876_success_1zv4vncjb,
    inst_evh_1040167445267876_success_9ye9qvljl,
    inst_evh_1040167445267876_submit_d2lnf77l9,
    submit: inst_evh_1040167445267876_submit_d2lnf77l9,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_x65fw8art,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_95kq15q0n = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_95kq15q0n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_i8czmwqlk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_i8czmwqlk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_7egxxfo2f = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_7egxxfo2f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_lwq2fxpvx = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_lwq2fxpvx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_itloszxcx = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_itloszxcx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_1zv4vncjb = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_1zv4vncjb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_9ye9qvljl = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_9ye9qvljl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_d2lnf77l9 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_d2lnf77l9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_x65fw8art = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_x65fw8art.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="SendMails"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="sendmail-options"
        id="zkhcfceubc"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="template"
          :label="gettext('Template')"
          :description="gettext('The template to use to generate the emails.')"
        >
          <ItpFormNativeSelect
            name="name"
            size="md"
            v-model="selectedTemplate"
            required
            :options="templates"
            valueField="id"
            textField="name"
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          form="zkhcfceubc"
          :text="gettext('Send')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
